//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getReadableDateRangeString } from '@components/exportable-report/ExportableReportHelpers';
import {
  BazaarvoiceLogo,
  PageNumber,
  Rectangle,
  ReportSectionCoverContainer,
} from '@components/reports/ReportSharedUI';
import { getSessionDateRange } from '@instagram-account/redux/selectors/getSessionDateRange';
import { isDefined } from '@utils/filterPredicates';

interface Props {
  dateRangeString?: string;
  pageNumber?: number;
  reportSectionTitle: string;
}

export const ReportSectionCover = ({
  dateRangeString,
  pageNumber,
  reportSectionTitle,
}: Props) => {
  const sessionDateRange = useSelector(getSessionDateRange);

  const defaultDateRangeString = getReadableDateRangeString(sessionDateRange);

  return (
    <ReportSectionCoverContainer>
      <DetailsContainer>
        <ReportSectionTitle>{reportSectionTitle}</ReportSectionTitle>
        <Rectangle />
        <DateString>{dateRangeString ?? defaultDateRangeString}</DateString>
      </DetailsContainer>
      <Footer>
        <StyledBazaarvoiceLogo />
        {isDefined(pageNumber) && (
          <PageNumberWrapper>
            <StyledPageNumber>{pageNumber}</StyledPageNumber>
          </PageNumberWrapper>
        )}
      </Footer>
    </ReportSectionCoverContainer>
  );
};

const DetailsContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const ReportSectionTitle = styled.div`
  font-size: 36px;
  font-weight: 600;
  line-height: 29px;
`;

const DateString = styled.div`
  font-size: 20px;
  line-height: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledBazaarvoiceLogo = styled(BazaarvoiceLogo)`
  padding-bottom: 40px;
`;

const PageNumberWrapper = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledPageNumber = styled(PageNumber)(
  ({ theme }) => `
  color: ${theme.colors.white};
  font-weight: ${theme.fontWeights.bold};
`,
);
